/* eslint-disable camelcase */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { memo, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import moment from 'moment-timezone';
import { showNotification } from '../../redux/notification';
import xhr from '../../utils/xhr';
import { ReactComponent as Guaranteed } from '../../assets/images/badge-guaranteed-inverse.svg';

import './CancelSlotModal.scss';

import BaseModal from '../../components/modals/BaseModal/BaseModal';
import Select from '../../components/form/Select';
import Radio from '../../components/form/Radio';
import { GLOBAL_CALENDAR_SHIFT_TYPES } from '../../constants';
import { facilitiesEnabled } from '../../redux/user';
import Checkbox from '../../components/form/Checkbox';
import utils from '../../utils/utils';

const REPLACEMENT_WORKER_OPTIONS = [
	{ label: 'Yes', value: 'yes', id: 'yes' },
	{ label: 'No', value: 'no', id: 'no' }
];

const CANCELATION_REASONS_WITH_REPLACEMENT = {
	[GLOBAL_CALENDAR_SHIFT_TYPES.FLEX]: [
		'SCHEDULED_BY_MISTAKE',
		'CALLED_OUT',
		'RESCHEDULED_TO_DIFFERENT_SHIFT',
		'UNEXPECTED_EVENT'
	],
	[GLOBAL_CALENDAR_SHIFT_TYPES.ON_DEMAND]: ['CREDENTIALS_OUT_OF_DATE']
};

const CANCELATION_REASONS_WITH_AGENCY = {
	[GLOBAL_CALENDAR_SHIFT_TYPES.FLEX]: [],
	[GLOBAL_CALENDAR_SHIFT_TYPES.ON_DEMAND]: ['SHIFT_FILLED_ANOTHER_AGENCY']
};

const CancelSlotModal = ({ onClose, onConfirm, shiftToCancel }) => {
	const { shift, slot } = shiftToCancel;
	const { shift_type: shiftType } = shift;
	const isFilled = useMemo(() => !!slot.caregiver_id, [slot]);
	const [cancellationReasons, setCancellationReasons] = useState([]);
	const [selectedReason, setSelectedReason] = useState();
	const [isLoading, setIsLoading] = useState(true);
	const [replacementWorkerNeeded, setReplacementWorkerNeeded] = useState(null);
	const [filledWithAgency, setFilledWithAgency] = useState(null);
	const dispatch = useDispatch();
	const userFacilities = useSelector(facilitiesEnabled, shallowEqual);
	const [acknowledgmentAccepted, setAcknowledgmentAccepted] = useState(false);
	const [uberAcknowledgmentAccepted, setUberAcknowledgmentAccepted] = useState(false);

	const isFlexShift = shiftType === GLOBAL_CALENDAR_SHIFT_TYPES.FLEX;

	useEffect(() => {
		const getPortalCancellationReasons = async () => {
			try {
				const filterQueryParams = new URLSearchParams();
				if (isFlexShift) {
					filterQueryParams.append('filterQuery', 'FLEX_SLOTS');
				} else if (isFilled === true) {
					filterQueryParams.append('filterQuery', 'CLAIMED_SLOTS');
				} else {
					filterQueryParams.append('filterQuery', 'NON_CLAIMED_SLOTS');
				}

				const { data } = await xhr.request('GET', `/entity/slot-cancelation-reasons?${filterQueryParams.toString()}`);
				setCancellationReasons(data);
			} catch {
				dispatch(showNotification('error', 'Failed to fetch portal cancellation reasons.'));
			}
		};
		getPortalCancellationReasons();
		setIsLoading(false);
	}, [dispatch, shiftType, isFilled]);

	const shiftFacility = useMemo(
		() => userFacilities.find((facility) => parseInt(facility.id, 10) === parseInt(shift.facility_id, 10)),
		[userFacilities, shift]
	);

	const isLateCancelation = useMemo(() => {
		if (shiftFacility && shiftFacility.facility_window_cancellation_hours) {
			const billableWindowStartTime = moment(shift.start_time)
				.tz(shift.facility_timezone)
				.subtract(shiftFacility.facility_window_cancellation_hours, 'hours');
			return isFilled && moment().tz(shift.facility_timezone).isAfter(billableWindowStartTime) && !slot.is_guaranteed;
		}
		return false;
	}, [isFilled, shiftFacility, shift, slot]);

	const showAcknowledgment =
		!isFlexShift &&
		isFilled &&
		(slot.is_guaranteed || (isLateCancelation && shiftFacility.facility_billable_cancellation_hours > 0));
	const showUberAcknowledgment = !!slot.has_uber_checkin_ride;

	const handleCancellationReasonChange = (e) => {
		setSelectedReason(e.cancellationReason);
		setReplacementWorkerNeeded(null);
	};

	const handleCancelButton = () => {
		onConfirm(
			selectedReason,
			replacementWorkerNeeded === 'yes',
			isLateCancelation,
			acknowledgmentAccepted || shiftFacility.facility_billable_cancellation_hours === 0,
			filledWithAgency,
			!isFilled,
			shiftFacility.facility_billable_cancellation_hours,
			shiftFacility.facility_window_cancellation_hours
		);
	};

	const isReadyToSubmit = () => {
		if (!selectedReason) {
			return false;
		}

		if (
			isFilled &&
			CANCELATION_REASONS_WITH_REPLACEMENT[shiftType].includes(selectedReason) &&
			replacementWorkerNeeded === null
		) {
			return false;
		}

		if (showAcknowledgment && !acknowledgmentAccepted) {
			return false;
		}

		if (showUberAcknowledgment && !uberAcknowledgmentAccepted) {
			return false;
		}

		return true;
	};

	return (
		<BaseModal
			className="cancel-slot-modal"
			title={isFilled ? `Cancel ${slot.caregiver_name}?` : 'Cancel shift'}
			primaryActionLabel="Cancel shift"
			onPrimaryAction={handleCancelButton}
			secondaryActionLabel="Go back"
			onSecondaryAction={onClose}
			primaryActionDisabled={!isReadyToSubmit()}
			onHide={onClose}
		>
			<div className="cancel-slot-modal__container">
				{isFilled && (
					<div className="cancel-slot-modal__filled-subtitle">
						Shifts worked at this facility:<span> {slot.worked ?? 0}</span>
					</div>
				)}
				<div className="cancel-slot-modal__column">
					Facility:<span> {shift.facility_name}</span>
				</div>
				<div className="cancel-slot-modal__column">
					Shift type:
					<span>
						{shift.shift_specialty
							? ` ${shift.shift_resource_type}-${shift.shift_specialty}`
							: ` ${shift.shift_resource_type}`}
					</span>
				</div>
				<div className="cancel-slot-modal__column">
					Date:<span> {moment(shift.start_time).tz(shift.facility_timezone).format('dddd[,] MMMM Do YYYY')}</span>
				</div>
				<div className="cancel-slot-modal__column">
					Time:<span> {`${shift.formatted_start_time} - ${shift.formatted_end_time}`}</span>
				</div>

				{!!slot.unit_name && (
					<div className="cancel-slot-modal__column">
						Unit:<span> {slot.unit_name}</span>
					</div>
				)}
				{shiftType === GLOBAL_CALENDAR_SHIFT_TYPES.ON_DEMAND && isFilled && !!slot.is_guaranteed && (
					<>
						<div className="cancel-slot-modal__column">
							Rate: <span>{`$${slot.facility_price}/Hr`}</span>
						</div>
						<div className="cancel-slot-modal__guaranteed">
							<Guaranteed /> GUARANTEED
						</div>
					</>
				)}

				<div className="cancel-slot-modal__column">
					<div className="cancel-slot-modal__reason">
						Select a reason for the cancellation
						<Select
							name="cancellationReason"
							placeholder="Select one..."
							value={selectedReason}
							options={cancellationReasons}
							handleChange={handleCancellationReasonChange}
							isDisabled={isLoading}
						/>
					</div>
					{isFilled && CANCELATION_REASONS_WITH_REPLACEMENT[shiftType].includes(selectedReason) && (
						<div className="cancel-slot-modal__replacement">
							Do you need a replacement worker?
							<Radio
								options={REPLACEMENT_WORKER_OPTIONS}
								name="replacement-worker-needed"
								selectedOption={replacementWorkerNeeded}
								handleChange={(e) => setReplacementWorkerNeeded(e)}
							/>
						</div>
					)}
					{CANCELATION_REASONS_WITH_AGENCY[shiftType].includes(selectedReason) && (
						<div className="cancel-slot-modal__agency">
							Which agency filled this shift?
							<input
								id="agency-filled-shift"
								type="text"
								placeholder="Agency name..."
								maxLength="50"
								value={filledWithAgency}
								onChange={(e) => setFilledWithAgency(e.target.value)}
								disabled={isLoading}
							/>
						</div>
					)}
				</div>
				{isFilled && showUberAcknowledgment && (
					<div className="cancel-slot-modal__acknoledgement">
						<Checkbox
							id="uber-acknowledgment"
							className="cancel-slot-modal__acknoledgement-checkbox"
							handleChange={(status) => setUberAcknowledgmentAccepted(status)}
						/>
						<label htmlFor="uber-acknowledgment">
							I acknowledge I am cancelling this shift knowing that this worker is committed to showing up and has
							scheduled their Uber ride to my facility.
						</label>
					</div>
				)}
				{!isFlexShift && showAcknowledgment && isFilled && (
					<div className="cancel-slot-modal__acknoledgement">
						<Checkbox
							id="acknowledgment"
							className="cancel-slot-modal__acknoledgement-checkbox"
							handleChange={(status) => setAcknowledgmentAccepted(status)}
						/>
						<label htmlFor="acknowledgment">
							{slot.is_guaranteed
								? 'I confirm that I am cancelling a Guaranteed Shift™. I understand that we will pay in full for this shift if I cancel it.'
								: `I confirm that I am cancelling a shift that begins in less than ${
										shiftFacility?.facility_window_cancellation_hours
								  } ${utils.pluralize(
										'hour',
										's',
										shiftFacility.facility_window_cancellation_hours
								  )}. I agree that we will pay the associated ${
										shiftFacility?.facility_billable_cancellation_hours
								  } hour cancellation fee for this shift.`}
						</label>
					</div>
				)}
			</div>
		</BaseModal>
	);
};

export default memo(CancelSlotModal);
